import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    IconButton,
    Popover,
    Box,
    Typography,
    Chip,
    Stack,
    Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from './Iconify';
import { TASK_MERGE_TAG_GROUPS } from '../utils/taskMergeTags';

/**
 * A small {📋} icon button that opens a popover listing all available
 * task merge-tag tokens grouped by category. Clicking a token inserts
 * `{{namespace.field}}` at the cursor position of the target TextField.
 *
 * Props:
 *   fieldRef  — React ref pointing at the MUI TextField's root element
 *               (the one whose inner <input> or <textarea> we'll mutate)
 *   value     — current text value of the field
 *   onChange  — called with the new string after insertion
 */
export default function TaskMergeTagPicker({ fieldRef, value, onChange }) {
    const [anchor, setAnchor] = useState(null);

    const insert = useCallback((token) => {
        const el = fieldRef?.current?.querySelector?.('textarea, input');
        const current = value || '';

        let next;
        if (el) {
            const start = el.selectionStart ?? current.length;
            const end = el.selectionEnd ?? current.length;
            const before = current.slice(0, start);
            const after = current.slice(end);
            // Add a space before the token if the preceding character isn't whitespace
            const spaceBefore = before.length > 0 && !/\s$/.test(before) ? ' ' : '';
            const spaceAfter = after.length > 0 && !/^\s/.test(after) ? ' ' : '';
            next = `${before}${spaceBefore}${token}${spaceAfter}${after}`;
            // Restore focus + move caret after the inserted token
            setTimeout(() => {
                el.focus();
                const pos = start + spaceBefore.length + token.length;
                el.setSelectionRange(pos, pos);
            }, 0);
        } else {
            next = `${current} ${token}`;
        }

        onChange(next);
        setAnchor(null);
    }, [fieldRef, value, onChange]);

    return (
        <>
            <Tooltip title="Insert merge tag">
                <IconButton
                    size="small"
                    onClick={(e) => setAnchor(e.currentTarget)}
                    sx={{
                        p: 0.5,
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main', bgcolor: alpha('#2563EB', 0.06) },
                    }}
                >
                    <Iconify icon="solar:code-square-bold-duotone" width={16} />
                </IconButton>
            </Tooltip>

            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        p: 1.5,
                        width: 280,
                        borderRadius: 2,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    },
                }}
            >
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Insert merge tag
                </Typography>
                <Stack spacing={1}>
                    {TASK_MERGE_TAG_GROUPS.map((group) => (
                        <Box key={group.label}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: group.color, fontSize: '0.68rem', display: 'block', mb: 0.5 }}>
                                {group.label}
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                {group.tags.map(({ token, label }) => (
                                    <Chip
                                        key={token}
                                        label={label}
                                        size="small"
                                        onClick={() => insert(token)}
                                        clickable
                                        sx={{
                                            height: 22,
                                            fontSize: '0.7rem',
                                            fontWeight: 500,
                                            bgcolor: alpha(group.color, 0.08),
                                            color: group.color,
                                            border: `1px solid ${alpha(group.color, 0.2)}`,
                                            '&:hover': {
                                                bgcolor: alpha(group.color, 0.15),
                                            },
                                            '& .MuiChip-label': { px: 0.75 },
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    ))}
                </Stack>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.62rem', display: 'block', mt: 1.25, lineHeight: 1.4 }}>
                    Tags resolve to live lead data at display time. Unknown tokens show as literal text.
                </Typography>
            </Popover>
        </>
    );
}

TaskMergeTagPicker.propTypes = {
    fieldRef: PropTypes.object.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
};
